/**
 * @zendir/ui - ClassificationBanner Component (Pure React)
 *
 * Security classification banner following Astro UX Design System.
 * Required for U.S. Space Force applications to display security classification.
 *
 * Features:
 * - Theme-integrated via useTheme()
 * - Official classification colors per DoD standards
 * - Top/bottom positioning
 * - Accessible (screen reader announces classification)
 */

import React, { memo } from "react";
import { useTheme } from "../theme";
import { classNames } from "../utils";

export type ClassificationLevel =
  | "unclassified"
  | "cui"
  | "controlled"
  | "confidential"
  | "secret"
  | "top-secret"
  | "top-secret-sci";

export interface ClassificationBannerProps {
  /** Classification level */
  classification: ClassificationLevel;
  /** Custom label (overrides default text) */
  label?: string;
  /** Position of the banner */
  position?: "top" | "bottom";
  /** Custom className */
  className?: string;
}

// Official DoD classification colors
const CLASSIFICATION_STYLES: Record<
  ClassificationLevel,
  { bg: string; text: string; label: string }
> = {
  unclassified: {
    bg: "#007a33",
    text: "#ffffff",
    label: "UNCLASSIFIED",
  },
  cui: {
    bg: "#502b85",
    text: "#ffffff",
    label: "CUI",
  },
  controlled: {
    bg: "#502b85",
    text: "#ffffff",
    label: "CONTROLLED",
  },
  confidential: {
    bg: "#0033a0",
    text: "#ffffff",
    label: "CONFIDENTIAL",
  },
  secret: {
    bg: "#c8102e",
    text: "#ffffff",
    label: "SECRET",
  },
  "top-secret": {
    bg: "#ff8c00",
    text: "#000000",
    label: "TOP SECRET",
  },
  "top-secret-sci": {
    bg: "#fce83a",
    text: "#000000",
    label: "TOP SECRET//SCI",
  },
};

/**
 * ClassificationBanner - Pure React classification banner
 *
 * Displays security classification level. Required for U.S. Space Force applications.
 *
 * @example
 * ```tsx
 * <ClassificationBanner classification="unclassified" />
 * <ClassificationBanner classification="secret" position="bottom" />
 * ```
 */
export const ClassificationBanner = memo(function ClassificationBanner({
  classification,
  label,
  position = "top",
  className = "",
}: ClassificationBannerProps): React.ReactElement {
  const { tokens } = useTheme();
  const style =
    CLASSIFICATION_STYLES[classification] ?? CLASSIFICATION_STYLES.unclassified;
  const displayLabel = label ?? style.label;

  return (
    <div
      className={classNames("zendir-classification-banner", className)}
      role="banner"
      aria-label={`Security classification: ${displayLabel}`}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        [position]: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 20,
        backgroundColor: style.bg,
        color: style.text,
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: tokens.typography.fontWeight.bold,
        letterSpacing: tokens.typography.letterSpacing.wide,
        textTransform: "uppercase",
        fontFamily: tokens.typography.fontFamily.primary,
        userSelect: "none",
      }}
    >
      {displayLabel}
    </div>
  );
});
